import type { AxiosError, AxiosResponse } from "axios";
import type {
  AgentEligibilityData,
  AgentEligibilityInput,
  ConversationTurn,
  EnableHelenaData,
  GenerateReplyInput,
  GenerateReplyReturn,
  GetConversationInput,
  GetUsageAnalyticsInput,
  HelenaAccessData,
  KBSearchResponse,
  KbKeywordSearchInput,
  RefineReplyInput,
  RefineReplyReturn,
  SubmitTriageReviewInput,
  SuggestedKbInput,
  SuggestedReplyInput,
  SuggestedReplyReturn,
  SuggestPromptsInput,
  SuggestPromptsReturn,
  TriageActivityData,
  TriageReviewResponse,
  TriageStatusData,
  UsageAnalyticsData,
} from "shared";
import { axiosClient } from "utilz";
import { apiErrorHandler } from "utilz/helpers";

const BASE_URL = import.meta.env.VITE_APP_TRIAGE_BASE_URL;

const HELENA_BASE_URL = import.meta.env.VITE_APP_HELENA_BASE_URL;

const HELENA_TRIAGE_BASE_URL = import.meta.env
  .VITE_APP_HELENA_TRIAGE_AGENT_BASE_URL;

export const getSuggestedReply = async ({
  ticketId,
}: SuggestedReplyInput): Promise<SuggestedReplyReturn | undefined> => {
  try {
    const response = await axiosClient.get(
      `${BASE_URL}/api/tickets/suggested-reply`,
      { params: { ticket_id: ticketId } },
    );
    const resData = response.data;
    if (resData.success) {
      return resData?.data;
    } else {
      throw response;
    }
  } catch (error: unknown) {
    apiErrorHandler(error as AxiosError | AxiosResponse);
  }
};

export const refineReply = async ({
  mspId,
  ticketId,
  currentReply,
  refineType,
}: RefineReplyInput): Promise<RefineReplyReturn | undefined> => {
  try {
    const response = await axiosClient.post(`${BASE_URL}/api/tickets/refine`, {
      mspId,
      ticket_id: ticketId,
      current_reply: currentReply,
      refine_type: refineType,
    });
    const resData = response.data;
    if (resData.success) {
      return resData?.data;
    } else {
      throw response;
    }
  } catch (error: unknown) {
    apiErrorHandler(error as AxiosError | AxiosResponse);
  }
};

export const getConversation = async ({
  ticketId,
}: GetConversationInput): Promise<ConversationTurn[]> => {
  try {
    const response = await axiosClient.get(
      `${BASE_URL}/api/tickets/conversation`,
      { params: { ticket_id: ticketId } },
    );
    const resData = response.data;
    if (resData.success) {
      return resData?.data?.turns ?? [];
    } else {
      throw response;
    }
  } catch (error: unknown) {
    return apiErrorHandler(error as AxiosError | AxiosResponse);
  }
};

export const generateReply = async ({
  mspId,
  ticketId,
  ticketTitle,
  ticketDescription,
  prompt,
  refineType,
}: GenerateReplyInput): Promise<GenerateReplyReturn | undefined> => {
  try {
    const response = await axiosClient.post(
      `${BASE_URL}/api/tickets/generate`,
      {
        mspId,
        ticket_id: ticketId,
        ticket_title: ticketTitle,
        ticket_description: ticketDescription,
        ...(prompt ? { prompt } : { refine_type: refineType }),
      },
    );
    const resData = response.data;
    if (resData.success) {
      return resData?.data;
    } else {
      throw response;
    }
  } catch (error: unknown) {
    return apiErrorHandler(error as AxiosError | AxiosResponse);
  }
};

export const suggestPrompts = async ({
  ticketTitle,
  ticketDescription,
  existingReply,
  conversationHistory,
}: SuggestPromptsInput): Promise<SuggestPromptsReturn | undefined> => {
  try {
    const response = await axiosClient.post(
      `${BASE_URL}/api/tickets/suggest-prompts`,
      {
        ticket_title: ticketTitle,
        ...(ticketDescription ? { ticket_description: ticketDescription } : {}),
        ...(existingReply ? { existing_reply: existingReply } : {}),
        ...(conversationHistory?.length
          ? { conversation_history: conversationHistory }
          : {}),
      },
    );
    const resData = response.data;
    if (resData.success) {
      return resData?.data;
    } else {
      throw response;
    }
  } catch (error: unknown) {
    return apiErrorHandler(error as AxiosError | AxiosResponse);
  }
};

export const getSuggestedKb = async ({
  summary,
  customer_id,
}: SuggestedKbInput) => {
  try {
    const response = await axiosClient.post(
      `${HELENA_BASE_URL}/api/process-kb-articles`,
      {
        summary,
        customer_id,
      },
    );
    const resData = response.data;
    if (resData.success) {
      return resData?.data;
    } else {
      throw response;
    }
  } catch (error: any) {
    apiErrorHandler(error);
  }
};

export const kbKeywordSearch = async ({
  query,
  customer_id,
}: KbKeywordSearchInput): Promise<KBSearchResponse> => {
  try {
    const response = await axiosClient.post(
      `${HELENA_BASE_URL}/api/kb/keyword-search`,
      {
        query,
        customer_id,
      },
    );
    const resData = response.data;
    if (resData.status === "success") {
      return resData?.data;
    } else {
      throw response;
    }
  } catch (error: any) {
    return apiErrorHandler(error);
  }
};

export const getKbDocContent = async ({
  docId,
}: {
  docId: string;
}): Promise<KBSearchResponse> => {
  try {
    const response = await axiosClient.post(
      `${HELENA_BASE_URL}/api/get-kb-document`,
      {
        doc_id: docId,
      },
    );
    const resData = response.data;
    if (resData.success) {
      return resData?.data;
    } else {
      throw response;
    }
  } catch (error: any) {
    return apiErrorHandler(error);
  }
};

export const getTriageStatus = async ({
  ticketId,
  mspId,
}: {
  ticketId: string;
  mspId: string;
}): Promise<TriageStatusData | undefined> => {
  try {
    const response = await axiosClient.get(
      `${HELENA_TRIAGE_BASE_URL}/api/tickets/${ticketId}/triage/status`,
      { params: { msp_id: mspId } },
    );
    const resData = response.data;
    if (resData.success) {
      return resData?.data;
    } else {
      throw response;
    }
  } catch (error: unknown) {
    apiErrorHandler(error as AxiosError | AxiosResponse);
  }
};

export const getAgentEligibility = async ({
  mspId,
  ticketId,
}: AgentEligibilityInput): Promise<AgentEligibilityData | undefined> => {
  try {
    const response = await axiosClient.post(
      `${HELENA_TRIAGE_BASE_URL}/api/agent/eligibility`,
      {
        msp_id: mspId,
        ticket_id: ticketId,
      },
    );
    const resData = response.data;
    if (resData.success) {
      return resData?.data;
    } else {
      throw response;
    }
  } catch (error: unknown) {
    return apiErrorHandler(error as AxiosError | AxiosResponse);
  }
};

export const getTriageActivity = async ({
  ticketId,
  mspId,
}: {
  ticketId: string;
  mspId: string;
}): Promise<TriageActivityData> => {
  try {
    const response = await axiosClient.get(
      `${HELENA_TRIAGE_BASE_URL}/api/tickets/${ticketId}/triage/activity`,
      { params: { msp_id: mspId } },
    );
    const resData = response.data;
    if (resData.success) {
      return resData?.data;
    }
    throw response;
  } catch (error: unknown) {
    return apiErrorHandler(error as AxiosError | AxiosResponse);
  }
};

/**
 * Submits the technician's review of a held phase. The reviewer identity comes
 * from the bearer token — never from the body. For an accept, `reviewedChanges`
 * must be the FULL (possibly edited) proposal, not a diff; an empty object
 * resumes the pipeline applying nothing.
 *
 * `action: "ignore"` is terminal — the server drops the proposals, sets the PSA
 * ticket to Manual and stops the agent for this ticket permanently. It ignores
 * `reviewed_changes`, so we don't send one. `action` is omitted for accepts so
 * the server's own default applies.
 *
 * `action: "ask_user"` (intake only) likewise ignores `reviewed_changes` — it
 * discards the hold and re-runs the clarification loop against the end user.
 */
export const submitTriageReview = async ({
  ticketId,
  mspId,
  phase,
  action,
  reviewedChanges,
}: SubmitTriageReviewInput): Promise<TriageReviewResponse | undefined> => {
  try {
    const carriesChanges = !action || action === "accept";
    const response = await axiosClient.post(
      `${HELENA_TRIAGE_BASE_URL}/api/tickets/${ticketId}/review`,
      {
        msp_id: mspId,
        phase,
        ...(action ? { action } : {}),
        ...(carriesChanges ? { reviewed_changes: reviewedChanges ?? {} } : {}),
      },
    );
    const resData = response.data;
    if (resData.success) {
      return resData?.data;
    } else {
      throw response;
    }
  } catch (error: unknown) {
    return apiErrorHandler(error as AxiosError | AxiosResponse);
  }
};

/**
 * `msp_id` is deliberately never sent — the tenant comes from the token, and
 * passing it is an admin override. `agent_id` scopes every metric AND every
 * comparison to one agent; an unknown id is not an error, it simply matches
 * zero tickets, so a wrong id surfaces as an empty window rather than a 4xx.
 */
export const getUsageAnalytics = async ({
  range,
  timezone,
  agentId,
}: GetUsageAnalyticsInput): Promise<UsageAnalyticsData> => {
  try {
    const response = await axiosClient.get(
      `${HELENA_TRIAGE_BASE_URL}/api/analytics/usage`,
      {
        params: {
          range,
          ...(timezone ? { timezone } : {}),
          ...(agentId ? { agent_id: agentId } : {}),
        },
      },
    );
    const resData = response.data;
    if (!resData?.success) throw response;

    const payload = (resData.data ?? resData) as UsageAnalyticsData;

    if (!payload?.metrics) throw response;

    return payload;
  } catch (error: unknown) {
    return apiErrorHandler(error as AxiosError | AxiosResponse);
  }
};

export const takeControl = async ({
  ticketId,
  mspId,
}: {
  ticketId: string;
  mspId: string;
}) => {
  try {
    const response = await axiosClient.post(
      `${HELENA_TRIAGE_BASE_URL}/api/tickets/${ticketId}/take-control`,
      { msp_id: mspId },
    );
    const resData = response.data;
    if (resData.success) {
      return resData?.data;
    } else {
      throw response;
    }
  } catch (error: unknown) {
    apiErrorHandler(error as AxiosError | AxiosResponse);
  }
};

export const getHelenaAccess = async (): Promise<
  HelenaAccessData | undefined
> => {
  try {
    const response = await axiosClient.get("/v1/helena/enabled");
    const resData = response.data;
    if (resData.success) {
      return resData?.data;
    } else {
      throw response;
    }
  } catch (error: unknown) {
    apiErrorHandler(error as AxiosError | AxiosResponse);
  }
};

export const enableHelena = async (): Promise<EnableHelenaData | undefined> => {
  try {
    const response = await axiosClient.post("/v1/helena/enabled");
    const resData = response.data;
    if (resData.success) {
      return resData?.data;
    } else {
      throw response;
    }
  } catch (error: unknown) {
    apiErrorHandler(error as AxiosError | AxiosResponse);
  }
};
